export default defineAppConfig({
  ui: {
    colors: {
      error: 'red',
      primary: 'amber',
      success: 'green',
      info: 'amber-old',
      secondary: 'peach',
      warning: 'amber-old'
    },

    header: {
      slots: {
        left: 'gap-6',
        header: 'px-4 sm:px-4',
        content: 'divide-none',
        body: 'px-4 pt-0 pb-4 sm:pt-0 sm:pb-4 sm:px-4',
        root: 'bg-muted backdrop-blur-none border-0',
        container: 'w-full max-w-full mx-0 sm:px-4 px-4 lg:px-3'
      }
    },

    navigationMenu: {
      slots: {
        link: 'text-xs',
        childLink: 'text-xs',
        linkTrailing: 'pointer-events-none'
      },
      variants: {
        variant: {
          link: 'bg-transparent hover:bg-transparent focus:bg-transparent before:bg-transparent hover:before:bg-transparent focus:before:bg-transparent'
        }
      },
      compoundVariants: [
        {
          variant: 'link',
          class: {
            link: 'p-3.5',
            list: 'gap-4'
          }
        },
        {
          active: true,
          disabled: false,
          variant: 'link',
          color: 'primary',
          class: {
            link: 'text-default hover:text-default before:bg-default hover:before:bg-default',
            linkLabelExternalIcon: 'text-default group-hover:text-default',
            childLinkLabelExternalIcon: 'text-default group-hover:text-default'
          }
        },
        {
          active: false,
          disabled: false,
          variant: 'link',
          color: 'primary',
          class: {
            link: 'text-toned hover:text-primary',
            childLink: 'text-toned hover:text-primary!',
            linkLabelExternalIcon: 'text-toned group-hover:text-primary',
            childLinkLabelExternalIcon: 'text-toned group-hover:text-primary'
          }
        },
        {
          orientation: 'vertical',
          collapsed: false,
          class: {
            childList: 'ms-3 border-0'
          }
        },
        {
          orientation: 'horizontal',
          highlight: true,
          class: {
            item: 'py-0',
            link: [
              'before:rounded-none before:inset-x-0',
              'after:absolute after:bottom-0 after:inset-x-0 after:block after:h-px'
            ]
          }
        }
      ],
      defaultVariants: {
        variant: 'link'
      }
    },

    tabs: {
      slots: {
        trigger: 'rounded'
      },
      variants: {
        variant: {
          buySell: {
            list: 'bg-elevated rounded-md',
            indicator: 'rounded-md inset-y-0',
            trigger: 'flex-1 data-[state=inactive]:not-disabled:text-default'
          },
          link: {
            list: 'bg-muted rounded-none p-0',
            trigger: 'font-normal rounded-none',
            indicator: 'bg-default rounded-none border-b border-primary'
          },
          pill: {
            list: 'rounded-md',
            indicator: 'rounded'
          },
          underline: {
            list: 'border-default',
            indicator: 'rounded-full',
            trigger: 'focus:outline-none'
          }
        }
      },
      compoundVariants: [
        {
          size: 'md',
          variant: 'buySell',
          class: {
            list: 'p-0',
            trigger: 'px-2.5 py-1.5 text-sm font-medium'
          }
        },
        {
          size: 'md',
          variant: 'link',
          class: {
            trigger: 'text-xs p-3'
          }
        },
        {
          orientation: 'horizontal',
          variant: 'link',
          class: {
            list: 'mb-0',
            indicator: 'h-auto top-0 bottom-0'
          }
        },
        {
          color: 'primary',
          variant: 'link',
          class: {
            indicator: 'bg-default border-b border-primary',
            trigger:
              'data-[state=active]:text-default data-[state=inactive]:text-toned hover:data-[state=inactive]:not-disabled:text-default focus-visible:ring-0 focus-visible:border-b focus-visible:border-primary/20'
          }
        },
        {
          color: 'neutral',
          variant: 'link',
          class: {
            indicator: 'bg-default border-b border-inverted',
            trigger:
              'data-[state=active]:text-highlighted data-[state=inactive]:text-toned hover:data-[state=inactive]:not-disabled:text-highlighted focus-visible:ring-0 focus-visible:border-b focus-visible:border-inverted/20'
          }
        },
        {
          variant: 'pill',
          color: 'neutral',
          class: {
            indicator: 'bg-default',
            trigger: 'data-[state=active]:text-highlighted'
          }
        },
        {
          size: 'md',
          variant: 'underline',
          class: {
            trigger: 'text-xs p-3'
          }
        },
        {
          color: 'primary',
          variant: 'underline',
          class: {
            indicator: 'bg-primary',
            trigger:
              'data-[state=active]:text-primary focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary'
          }
        },
        {
          color: 'neutral',
          variant: 'underline',
          class: {
            indicator: 'bg-inverted',
            trigger:
              'data-[state=active]:text-highlighted focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-inverted'
          }
        },
        {
          orientation: 'vertical',
          variant: 'underline',
          class: {
            list: 'border-s -ms-px',
            indicator: '-start-px w-px'
          }
        },
        {
          orientation: 'horizontal',
          variant: 'underline',
          class: {
            list: 'border-b -mb-px',
            indicator: '-bottom-px h-px'
          }
        }
      ]
    },

    table: {
      slots: {
        loading: 'py-12',
        separator: 'hidden',
        empty: 'text-xs py-12 max-sm:text-left max-sm:pl-12',
        td: 'text-xs text-default px-2.5 py-2',
        th: 'text-xs text-muted p-2.5 font-normal whitespace-nowrap'
      },
      variants: {
        sticky: {
          true: {
            thead: 'bg-muted backdrop-blur-none'
          },
          header: {
            thead: 'bg-muted backdrop-blur-none'
          }
        }
      }
    },

    selectMenu: {
      slots: {
        content: 'min-w-fit rounded',
        item: 'not-data-disabled:cursor-pointer rounded-sm before:bg-transparent! hover:not-data-disabled:text-primary! hover:not-data-disabled:bg-elevated data-highlighted:not-data-disabled:text-primary! data-highlighted:not-data-disabled:bg-elevated data-[state=checked]:text-primary! data-[state=checked]:bg-elevated',
        trailingIcon:
          'group-data-[state=open]:rotate-180 transition-transform duration-200'
      }
    },

    select: {
      slots: {
        item: 'data-highlighted:text-primary! data-highlighted:bg-elevated data-highlighted:before:bg-transparent data-[state=checked]:text-primary! data-[state=checked]:bg-elevated cursor-pointer'
      }
    },

    slider: {
      slots: {
        track: 'ring-1 ring-default',
        range: 'rounded-r-none',
        thumb: 'z-20 bg-elevated'
      },
      variants: {
        color: {
          primary: {
            range: 'bg-accented',
            thumb: 'bg-elevated',
            track: 'bg-elevated'
          }
        }
      }
    },

    stepper: {
      variants: {
        color: {
          primary: {
            separator: 'bg-primary group-data-[state=completed]:bg-success',
            trigger:
              'bg-primary/10 text-primary group-data-[state=completed]:bg-success/10 group-data-[state=completed]:text-success group-data-[state=active]:bg-primary/10 group-data-[state=active]:text-primary focus-visible:outline-primary'
          }
        }
      }
    },

    button: {
      slots: {
        base: 'rounded-md'
      },
      variants: {
        color: {
          neutral: 'disabled:text-dimmed aria-disabled:text-dimmed'
        },
        size: {
          '2xs': {
            base: 'rounded px-1.5 py-0.5 text-2xs gap-0.5',
            leadingIcon: 'size-3',
            leadingAvatarSize: '3xs',
            trailingIcon: 'size-3'
          },
          '3xs': {
            base: 'rounded px-1 py-px text-2xs gap-0.5',
            leadingIcon: 'size-3',
            leadingAvatarSize: '3xs',
            trailingIcon: 'size-3'
          }
        }
      },
      compoundVariants: [
        { size: '2xs', square: true, class: 'p-0.5' },
        { size: '3xs', square: true, class: 'p-px' }
      ]
    },

    checkbox: {
      variants: {
        disabled: {
          true: {
            label: 'text-dimmed'
          }
        }
      },
      defaultVariants: {
        size: 'sm'
      }
    },

    pinInput: {
      variants: {
        variant: {
          subtle:
            'text-default bg-elevated focus-visible:ring! placeholder:text-dimmed'
        }
      }
    },

    input: {
      variants: {
        variant: {
          subtle:
            'text-default bg-elevated focus-visible:ring! placeholder:text-dimmed'
        }
      },
      compoundVariants: [
        {
          color: 'error',
          variant: 'subtle',
          class: 'ring ring-error'
        },
        {
          fixed: false,
          size: 'md',
          class: 'text-sm'
        },
        {
          fixed: false,
          size: 'lg',
          class: 'text-sm'
        }
      ]
    },

    badge: {
      slots: {
        base: 'rounded-md'
      },
      compoundVariants: [
        {
          color: 'neutral',
          variant: 'outline',
          class: 'opacity-60 bg-transparent ring-neutral-500 text-neutral-100'
        }
      ]
    },

    container: {
      base: 'px-3 sm:px-4 lg:px-4'
    },

    modal: {
      slots: {
        content: 'bg-muted',
        wrapper: 'inline-flex',
        body: 'sm:px-3 sm:py-4',
        footer: 'gap-3 p-3 sm:px-3',
        header: 'relative sm:p-3 px-3 sm:py-2 py-2 min-h-12',
        close: 'top-1/2 -translate-y-1/2 right-1.5 text-dimmed'
      }
    }
  }
})
